/**
 * @file nav-utils.ts
 * @brief Shared helpers for flattening and matching mega-menu nav trees.
 *
 * @author DES
 * @date 06-07-2026
 */

import type { NavGroup, NavLinkItem, NavShellConfig } from "./nav-types";

export function navItemKey(item: NavLinkItem): string {
  return item.id ?? item.to;
}

export function parseNavTarget(to: string): { path: string; hash: string } {
  const hashIndex = to.indexOf("#");
  if (hashIndex === -1) {
    return { path: to, hash: "" };
  }
  return {
    path: to.slice(0, hashIndex),
    hash: to.slice(hashIndex),
  };
}

function matchesPath(pathname: string, path: string, end?: boolean): boolean {
  if (end) return pathname === path;
  return pathname === path || pathname.startsWith(`${path}/`);
}

export function matchesLocation(
  pathname: string,
  hash: string,
  item: NavLinkItem,
): boolean {
  const { path, hash: itemHash } = parseNavTarget(item.to);
  if (!matchesPath(pathname, path, item.end)) return false;
  if (itemHash) return hash === itemHash;
  return hash === "";
}

export function isNavItemActive(
  pathname: string,
  hash: string,
  item: NavLinkItem,
  parent?: NavLinkItem,
): boolean {
  if (!matchesLocation(pathname, hash, item)) return false;

  const itemTarget = parseNavTarget(item.to);
  if (parent) {
    const parentTarget = parseNavTarget(parent.to);
    if (
      itemTarget.path === parentTarget.path &&
      !itemTarget.hash &&
      parentTarget.hash === ""
    ) {
      return false;
    }
  }

  if (item.children?.length) {
    const specificChild = item.children.find((child) =>
      matchesLocation(pathname, hash, child),
    );
    if (specificChild) return false;
    return itemTarget.path === pathname && hash === "";
  }

  return true;
}

/**
 * Active state for topbar/footer highlight tabs when paths nest
 * (e.g. `/company/meetings/new` vs `/company/meetings`).
 * Only the longest matching highlight stays active.
 */
export function isHighlightActive(
  pathname: string,
  hash: string,
  item: NavLinkItem,
  highlights: NavLinkItem[],
): boolean {
  if (!matchesLocation(pathname, hash, item)) return false;

  const itemPathLen = parseNavTarget(item.to).path.length;
  return !highlights.some((other) => {
    if (other.to === item.to) return false;
    if (!matchesLocation(pathname, hash, other)) return false;
    return parseNavTarget(other.to).path.length > itemPathLen;
  });
}

export function isNavBranchActive(
  pathname: string,
  hash: string,
  item: NavLinkItem,
): boolean {
  if (!item.children?.length) {
    return isNavItemActive(pathname, hash, item);
  }
  const { path } = parseNavTarget(item.to);
  if (!matchesPath(pathname, path, item.end)) return false;
  return true;
}

export function flattenGroupItems(items: NavLinkItem[]): NavLinkItem[] {
  return items.flatMap((item) =>
    item.children?.length ? [item, ...item.children] : [item],
  );
}

export function flattenGroups(groups: NavGroup[]): NavLinkItem[] {
  return groups.flatMap((group) => flattenGroupItems(group.items));
}

export function countBrowsablePages(groups: NavGroup[]): number {
  return flattenGroups(groups).length;
}

function itemMatchesQuery(item: NavLinkItem, query: string): boolean {
  return (
    item.label.toLowerCase().includes(query) ||
    (item.description?.toLowerCase().includes(query) ?? false)
  );
}

export function filterNavGroups(
  groups: NavGroup[],
  rawQuery: string,
): NavGroup[] {
  const query = rawQuery.trim().toLowerCase();
  if (!query) return groups;

  return groups
    .map((group) => ({
      ...group,
      items: group.items
        .map((item) => {
          const matchingChildren =
            item.children?.filter((child) => itemMatchesQuery(child, query)) ??
            [];

          if (itemMatchesQuery(item, query)) {
            return item;
          }

          if (matchingChildren.length > 0) {
            return { ...item, children: matchingChildren };
          }

          return null;
        })
        .filter((item): item is NavLinkItem => item !== null),
    }))
    .filter((group) => group.items.length > 0);
}

export function resolveActiveNavLabel(
  highlights: NavLinkItem[],
  groups: NavGroup[],
  pathname: string,
  hash: string,
): string | null {
  const candidates: NavLinkItem[] = [];

  const collect = (item: NavLinkItem, parent?: NavLinkItem) => {
    if (isNavItemActive(pathname, hash, item, parent)) {
      candidates.push(item);
    }
    item.children?.forEach((child) => collect(child, item));
  };

  for (const item of highlights) collect(item);
  for (const group of groups) {
    for (const item of group.items) collect(item);
  }

  if (candidates.length > 0) {
    return (
      candidates.sort((a, b) => b.to.length - a.to.length)[0]?.label ?? null
    );
  }

  const branch = groups
    .flatMap((group) => group.items)
    .find((item) => isNavBranchActive(pathname, hash, item));

  return branch?.label ?? null;
}

/**
 * `resolveActiveNavLabel` for a whole shell config — the shape the top bar has.
 * Moved out of `NavMegaMenu.tsx` (02-08-2026): that module renders a component,
 * and a module exporting both a component and a value loses React Fast Refresh.
 * It sits here, next to the primitive it wraps.
 */
export function resolveNavShellLabel(
  config: NavShellConfig,
  pathname: string,
  hash = "",
): string | null {
  return resolveActiveNavLabel(config.highlights, config.groups, pathname, hash);
}
